import { ReactNode } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, CheckCircle, Info, XCircle, X } from 'lucide-react';

export interface AlertProps {
  variant?: 'info' | 'success' | 'warning' | 'danger';
  title?: string;
  children: ReactNode;
  icon?: ReactNode;
  onDismiss?: () => void;
  className?: string;
}

const Alert = ({
  variant = 'info',
  title,
  children,
  icon,
  onDismiss,
  className,
}: AlertProps) => {
  const variants = {
    info: {
      container: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
      icon: 'text-blue-500 dark:text-blue-400',
      title: 'text-blue-800 dark:text-blue-300',
      content: 'text-blue-700 dark:text-blue-400',
    },
    success: {
      container: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
      icon: 'text-green-500 dark:text-green-400',
      title: 'text-green-800 dark:text-green-300',
      content: 'text-green-700 dark:text-green-400',
    },
    warning: {
      container: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
      icon: 'text-amber-500 dark:text-amber-400',
      title: 'text-amber-800 dark:text-amber-300',
      content: 'text-amber-700 dark:text-amber-400',
    },
    danger: {
      container: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
      icon: 'text-red-500 dark:text-red-400',
      title: 'text-red-800 dark:text-red-300',
      content: 'text-red-700 dark:text-red-400',
    },
  };

  const defaultIcons = {
    info: <Info className="h-5 w-5" />,
    success: <CheckCircle className="h-5 w-5" />,
    warning: <AlertTriangle className="h-5 w-5" />,
    danger: <XCircle className="h-5 w-5" />,
  };

  const styles = variants[variant];

  return (
    <div
      className={clsx(
        'rounded-xl border p-4',
        styles.container,
        className
      )}
    >
      <div className="flex gap-3">
        <div className={clsx('shrink-0', styles.icon)}>
          {icon || defaultIcons[variant]}
        </div>
        <div className="flex-1 min-w-0">
          {title && (
            <h3 className={clsx('text-sm font-semibold', styles.title)}>{title}</h3>
          )}
          <div className={clsx('text-sm', title && 'mt-1', styles.content)}>
            {children}
          </div>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={clsx(
              'shrink-0 rounded-lg p-1 hover:bg-black/5 dark:hover:bg-white/5 transition-colors',
              styles.icon
            )}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export { Alert };
